import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { 
  Search, User, 
  Menu, X, ChevronDown, Building2, PaintBucket, TrendingUp,
  FileText, Briefcase, Sparkles, Wallet, Phone, Heart, Home, Key, Globe
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useCountry } from '../context/CountryContext';
import AIChatOverlay from './AIChatOverlay';
import { useAuthModal } from '../context/AuthModalContext';

// --- Types & Interfaces ---

interface NavItemProps {
  to: string;
  children: React.ReactNode;
  badge?: string;
  isTransparent?: boolean;
}

interface NavDropdownProps {
  id: string;
  label: string;
  icon?: React.ElementType;
  items: { label: string, to: string, icon?: React.ElementType, desc?: string }[];
  isTransparent?: boolean;
  isOpen: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
}

// --- Sub-Components ---

const NavItem = ({ to, children, badge, isTransparent }: NavItemProps) => (
  <NavLink 
    to={to} 
    className={({ isActive }) => 
      `relative px-2 py-2 text-[14px] font-medium font-sans tracking-normal transition-all rounded-md group ${
        isActive 
          ? (isTransparent ? 'text-white' : 'text-[#FF4D00]')
          : (isTransparent 
              ? 'text-white/80 hover:text-white' 
              : 'text-[#1E4164] hover:text-[#1E4164]')
      }`
    }
  >
    <span className="relative z-10 flex items-center gap-2">
      {children}
      {badge && (
        <span className="px-1.5 py-0.5 text-[9px] font-black text-white bg-[#FF4D00] rounded-md shadow-lg shadow-orange-500/20">
          {badge}
        </span>
      )}
    </span>
  </NavLink>
);

const NavDropdown = ({ label, items, icon: Icon, isTransparent, isOpen, onMouseEnter, onMouseLeave, onClick }: Omit<NavDropdownProps, 'id'>) => {
  return (
    <div 
      className="relative h-full flex items-center" 
      onMouseEnter={onMouseEnter} 
      onMouseLeave={onMouseLeave}
    >
      <button 
        onClick={onClick}
        className={`flex items-center gap-1.5 px-2 py-2 text-[14px] font-medium font-sans tracking-normal rounded-md transition-all ${
          isOpen 
            ? (isTransparent ? 'text-white' : 'text-[#FF4D00]')
            : (isTransparent 
                ? 'text-white/80 hover:text-white' 
                : 'text-[#1E4164] hover:text-[#1E4164]')
        }`}
      >
        {Icon && <Icon className="w-4 h-4 opacity-80" />}
        {label}
        <ChevronDown 
          className={`w-3.5 h-3.5 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''}`} 
          strokeWidth={2.5}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            layoutId="dropdown-content"
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="absolute top-full start-0 pt-3 z-50 w-64"
          >
            <div className="bg-white dark:bg-[#18181B] rounded-md border border-slate-200 dark:border-white/10 overflow-hidden shadow-[0_6px_16px_rgba(0,0,0,0.12)] py-2">
              <div className="flex flex-col">
                {items.map((item, idx) => (
                  <Link 
                    key={idx} 
                    to={item.to}
                    className="px-4 py-3 text-[14px] text-[#222222] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors font-medium text-start"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Main Component ---

const Navbar = ({ position = 'sticky', forceTransparent = false }: { position?: 'fixed' | 'absolute' | 'sticky', forceTransparent?: boolean }) => {
  const { t, i18n } = useTranslation();
  const { user, isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { countries, activeCountry, setActiveCountry } = useCountry();
  const { openLogin } = useAuthModal();
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);

  // Centralized State for Dropdowns
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);

  // --- Helper: Get Country-Prefixed Path ---
  const getPath = (path: string) => {
      if (path.startsWith('http') || path.startsWith('/admin')) return path;
      const prefix = activeCountry ? `/${activeCountry.code.toLowerCase()}` : '';
      if (path === '/') return prefix || '/';
      return `${prefix}${path.startsWith('/') ? path : '/' + path}`;
  };

  const handleDropdownClick = (id: string) => {
      setActiveDropdownId(prev => prev === id ? null : id);
  };
 
  // Close menus on outside click or route change
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setUserMenuOpen(false);
    setActiveDropdownId(null);
    setIsAiChatOpen(false);
  }, [location.pathname]);

  // Listen for custom event to open AI Chat
  useEffect(() => {
    const handleOpenChat = () => setIsAiChatOpen(true);
    document.addEventListener('open-ai-chat', handleOpenChat);
    return () => document.removeEventListener('open-ai-chat', handleOpenChat);
  }, []);

  // Handle Scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      setActiveDropdownId(null);
      setUserMenuOpen(false);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    setScrolled(window.scrollY > 10);
  }, []);
  // Use prop or scroll state
  const isTransparent = forceTransparent || (!scrolled && position === 'absolute');
  
  return (
    <>
      <header 
        className={`${position} top-0 left-0 right-0 z-[100] transition-all duration-300 bg-white dark:bg-[#09090b] py-4 ${scrolled ? 'shadow-sm' : 'shadow-none'}`}
      >
        <div className="w-full max-w-[1460px] 2xl:max-w-[1520px] mx-auto px-6 md:px-12 lg:px-20 xl:px-24 2xl:px-20">
          <div className="flex items-center justify-between">
            
            {/* LEFT: Branding & Main Nav */}
            <div className="flex items-center gap-8 md:gap-12">
              
              {/* LOGO */}
              <Link to={getPath('/')} className="flex items-center group shrink-0 relative z-20">
                <div className="relative flex items-center">
                  <img 
                    src="/laqysymbolandlogo_premium.svg" 
                    alt="Laqy Estate" 
                    className="h-5 md:h-7 w-auto object-contain transition-all duration-500 ease-out"
                  />
                </div>
              </Link>

              {/* DESKTOP NAVIGATION (Simplified Gold Standard) */}
              <nav ref={navRef} className="hidden lg:flex items-center gap-1 lg:gap-2 bg-blend-difference">
                <NavItem to={getPath("/search?type=sale")} isTransparent={isTransparent}>{t('nav_buy', 'Buy')}</NavItem>
                <NavItem to={getPath("/rent")} isTransparent={isTransparent}>{t('nav_rent', 'Rent')}</NavItem>
                <NavItem to={getPath("/sell")} isTransparent={isTransparent}>{t('nav_sell', 'Sell')}</NavItem>
                <NavItem to={getPath("/commercial")} isTransparent={isTransparent}>{t('nav_commercial', 'Commercial')}</NavItem>




                <NavDropdown 
                  label={t('nav_explore', 'Explore')}
                  isTransparent={isTransparent}
                  isOpen={activeDropdownId === 'explore'}
                  onClick={() => handleDropdownClick('explore')}
                  // Static click behavior enabled
                  items={[
                    { label: t('nav_exclusive_collections', 'Exclusive Collections'), to: getPath('/launches'), icon: Sparkles, desc: t('nav_exclusive_desc', 'Newest premium projects') },
                    { label: t('nav_investment_center', 'Investment Center'), to: getPath('/investment'), icon: TrendingUp, desc: t('nav_investment_desc', 'Portfolio & Market insights') },
                    { label: t('nav_financing_solutions', 'Financing Solutions'), to: getPath('/move-now-pay-later'), icon: Wallet, desc: t('nav_financing_desc', 'Flexible payment plans') },
                    { label: t('nav_partners', 'Partners'), to: getPath('/explore-partners'), icon: PaintBucket, desc: t('nav_partners_desc', 'Design & Finishing') },
                    { label: t('nav_developers', 'Developers'), to: getPath('/developers'), icon: Building2, desc: t('nav_developers_desc', 'Top real estate partners') },
                    { label: t('nav_brokers', 'Find a Broker'), to: getPath('/brokers'), icon: Briefcase, desc: t('nav_brokers_desc', 'Connect with experts') },
                    { label: t('nav_blog', 'Blog & News'), to: getPath('/blog'), icon: FileText, desc: t('nav_blog_desc', 'Latest real estate news') },
                    { label: t('nav_contact_us', 'Contact Us'), to: getPath('/contact'), icon: Phone, desc: t('nav_contact_desc', 'Get in touch with us') },
                  ]}
                />
              </nav>
            </div>

            {/* RIGHT Actions */}
            <div className="flex items-center gap-4 sm:gap-6">
              
              {/* Icons Group */}
              <div className="flex items-center gap-2">


                  {/* ✅ Ask AI Button (Clean Text Link - No Hover BG) */}
                   <button 
                    onClick={() => setIsAiChatOpen(true)}
                    className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-[14px] font-medium font-sans transition-all rounded-md ${
                      !isTransparent
                        ? 'text-[#1E4164] hover:text-[#1E4164]'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    <Sparkles className="w-4 h-4" strokeWidth={2} />
                    <span>{t('nav_ask_ai')}</span>
                  </button>

                  <div className={`hidden md:block w-px h-4 mx-1 ${!isTransparent ? 'bg-gray-300' : 'bg-white/20'}`}></div>

                  <button
                    onClick={() => {
                        const next = i18n.language === 'en' ? 'ar' : 'en';
                        i18n.changeLanguage(next);
                    }}
                    className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-[14px] font-medium font-sans transition-all rounded-md ${
                      !isTransparent
                        ? 'text-[#1E4164] hover:text-[#1E4164]'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    <Globe className="w-4 h-4" strokeWidth={2} />
                    <span>{i18n.language === 'en' ? 'العربية' : 'English'}</span>
                  </button>

                {isLoggedIn && (
                  <Link 
                    to={getPath("/favorites")}
                    className={`p-2 rounded-md transition-all duration-300 hover:scale-105 active:scale-95 hidden sm:block ${
                      !isTransparent 
                        ? 'text-[#1E4164] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5' 
                        : 'text-white hover:bg-white/20 backdrop-blur-md'
                    }`}
                  >
                    <Heart className="w-5 h-5" />
                  </Link>
                )}

                {/* Admin Dashboard Quick Access */}
                {isLoggedIn && user && (user.is_admin || user.is_superadmin) && (
                  <Link 
                    to="/admin"
                    className={`p-2 rounded-md transition-all duration-300 hover:scale-105 active:scale-95 hidden sm:block ${
                      !isTransparent 
                        ? 'text-[#1E4164] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5' 
                        : 'text-white hover:bg-white/20 backdrop-blur-md'
                    }`}
                    title={t('auth_admin_dashboard')}
                  >
                    <Briefcase className="w-5 h-5" />
                  </Link>
                )}
              </div>



                {isLoggedIn && user ? (
                  <div 
                    className="relative" 
                    ref={userMenuRef}
                  >
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className={`relative w-9 h-9 flex items-center justify-center rounded-full transition-all duration-200 ${
                        !isTransparent
                          ? 'bg-gray-100 text-[#1E4164] hover:bg-gray-100 hover:text-[#1E4164] ring-2 ring-transparent'
                          : 'bg-white/20 text-white hover:bg-white/30'
                      }`}
                    >
                       <User className="w-5 h-5" strokeWidth={2} />
                    </button>

                    <AnimatePresence>
                      {userMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.96 }}
                          transition={{ type: "spring", stiffness: 400, damping: 30 }}
                          className="absolute end-0 top-full mt-2 w-64 z-50 origin-top-right rtl:origin-top-left"
                        >
                          <div className="bg-white dark:bg-[#18181B] rounded-md border border-slate-200 dark:border-white/10 overflow-hidden shadow-[0_6px_16px_rgba(0,0,0,0.12)] py-2">
                            
                            {/* User Header (Simplified) */}
                            <div className="px-4 py-3 border-b border-slate-200 dark:border-white/5 mb-1">
                               <div className="text-[14px] font-medium font-sans text-[#212427] dark:text-white truncate">{user.email}</div>
                               <div className="text-[12px] font-sans text-gray-500 font-normal">
                                 {(user.is_admin || user.is_superadmin) ? t('auth_admin_account', 'Admin Account') : 
                                  user.developer_profile || user.role === 'DEVELOPER' ? t('auth_developer_account', 'Developer Account') :
                                  user.broker_profile?.is_approved ? t('auth_broker_account', 'Broker Account') : 
                                  t('auth_personal_account', 'Personal Account')}
                               </div>
                            </div>

                              <div className="flex flex-col">
                                  {[
                                  { to: getPath("/profile"), label: t('nav_profile') },
                                  { to: getPath("/favorites"), label: t('nav_favorites') },
                                  ...(user.broker_profile?.is_approved ? [{ to: getPath("/broker/dashboard"), label: t('nav_broker_dashboard', 'Broker Terminal') }] : []),
                                  ...(user.developer_profile || user.role === 'DEVELOPER' ? [{ to: getPath("/developer/dashboard"), label: t('nav_developer_dashboard', 'Developer Terminal') }] : []),
                                  ...((user.is_superadmin || user.is_admin) ? [{ to: "/admin", label: t('nav_admin') }] : []),
                                ].map((item, idx) => (
                                  <Link 
                                    key={idx} 
                                    to={item.to}
                                    onClick={() => setUserMenuOpen(false)}
                                    className="px-4 py-3 text-[14px] text-[#212427] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors font-medium font-sans text-start"
                                  >
                                    {item.label}
                                  </Link>
                                ))}

                               {/* Language Switcher Button (Simplified) */}
                               <button
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      const current = i18n.language || 'en';
                                      const next = current === 'en' ? 'ar' : 'en';
                                      i18n.changeLanguage(next);
                                  }}
                                  className="px-4 py-3 text-[14px] text-[#212427] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors font-medium font-sans text-start w-full flex items-center justify-between"
                               >
                                  <span>{t('nav_language')}</span>
                                  <span className="text-gray-400 text-xs uppercase">{i18n.language === 'en' ? 'EN' : 'AR'}</span>
                               </button>
                            </div>

                            <div className="border-t border-slate-200 dark:border-white/5 my-1"></div>

                            <button
                              onClick={() => {
                                logout();
                                setUserMenuOpen(false);
                              }}
                              className="w-full text-start px-4 py-3 text-[14px] font-medium font-sans text-[#FF385C] hover:bg-gray-50 dark:hover:bg-white/10 transition-colors"
                            >
                              {t('nav_logout')}
                            </button>

                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <button
                    onClick={openLogin}
                    className={`hidden md:flex items-center justify-center h-9 px-4 rounded-full text-[14px] font-bold font-sans transition-all duration-200 border ${
                      !isTransparent
                        ? 'border-slate-200 text-[#212427] hover:border-gray-300 hover:bg-gray-50'
                        : 'border-white/30 text-white hover:bg-white/10'
                    }`}
                  >
                    {t('nav_login', 'Log in')}
                  </button>
                )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`lg:hidden p-2 rounded-md transition-colors ${
                   scrolled
                     ? 'text-[#666666] dark:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                     : 'text-white hover:bg-white/10'
                }`}
              >
                <AnimatePresence mode="wait">
                  {isMobileMenuOpen ? (
                    <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
                      <X className="w-6 h-6" />
                    </motion.div>
                  ) : (
                    <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
                      <Menu className="w-6 h-6" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            </div>
          </div>

          {/* MOBILE MENU */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="lg:hidden border-t border-slate-200 dark:border-white/5 bg-white dark:bg-[#0A0A0A] overflow-hidden rounded-b-3xl shadow-2xl absolute top-full left-0 right-0"
              >
                <div className="p-4 flex flex-col gap-2 max-h-[80vh] overflow-y-auto pb-8">
                  <div className="relative mb-4">
                    <Search className="absolute start-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input 
                      type="text" 
                      placeholder={t('search_placeholder')}
                      className="w-full ps-11 pe-4 py-3.5 bg-gray-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-md text-[14px] font-medium font-sans focus:ring-2 focus:ring-black dark:focus:ring-white outline-none transition-all"
                      onClick={() => { navigate(getPath('/search')); setIsMobileMenuOpen(false); }}
                    />
                  </div>
                  
                  {/* Ask AI in Mobile Menu */}
                   <button 
                      onClick={() => { setIsMobileMenuOpen(false); setIsAiChatOpen(true); }}
                      className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 rounded-md transition-all active:scale-[0.98] group"
                    >
                      <div className="p-2 bg-white dark:bg-white/10 rounded-md group-hover:text-[#FF4D00] transition-colors"><Sparkles className="w-4 h-4 text-[#FF4D00]" /></div> 
                      {t('nav_ask_ai_assist')}
                  </button>

                  <div className="space-y-1 mt-2">
                    <div className="text-[10px] font-bold font-sans tracking-widest text-gray-400 px-4 py-2 mt-2">{t('nav_menu')}</div>
                    <Link to={getPath("/search?type=sale")} className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 rounded-md transition-all active:scale-[0.98]">
                        <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-md"><Home className="w-4 h-4" /></div> {t('nav_properties_sale')}
                    </Link>
                    <Link to={getPath("/rent")} className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 rounded-md transition-all active:scale-[0.98]">
                        <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-md"><Key className="w-4 h-4" /></div> {t('nav_rent', 'Rent')}
                    </Link>
                    <Link to={getPath("/launches")} className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 rounded-md transition-all active:scale-[0.98]">
                        <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-md"><Sparkles className="w-4 h-4" /></div> {t('nav_exclusive_collections', 'Exclusive Collections')}
                    </Link>
                    <Link to={getPath("/move-now-pay-later")} className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 rounded-md transition-all active:scale-[0.98]">
                        <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-md"><Wallet className="w-4 h-4" /></div> {t('nav_financing_solutions', 'Financing Solutions')}
                    </Link>
                     <Link to={getPath("/compounds")} className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 rounded-md transition-all active:scale-[0.98]">
                        <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-md"><Building2 className="w-4 h-4" /></div> {t('home_cat_compounds', 'Compounds')}
                    </Link>
                    <Link to={getPath("/sell")} className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 rounded-md transition-all active:scale-[0.98]">
                        <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-md"><Wallet className="w-4 h-4" /></div> {t('nav_sell_property')}
                    </Link>
                    <Link to={getPath("/contact")} className="flex items-center gap-4 px-4 py-3.5 text-[14px] font-medium font-sans text-[#212427] dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 rounded-md transition-all active:scale-[0.98]">
                        <div className="p-2 bg-gray-100 dark:bg-white/10 rounded-md"><Phone className="w-4 h-4" /></div> {t('nav_contact_us', 'Contact Us')}
                    </Link>
                  </div>

                  <div className="my-2 border-t border-slate-200 dark:border-white/5" />

                  <div className="px-4 py-2 flex items-center justify-between">
                    <span className="text-[14px] font-medium font-sans text-gray-500 dark:text-gray-400">{t('nav_region')}</span>
                     <select 
                          value={activeCountry?.id || ''}
                          onChange={(e) => {
                              const c = countries.find(x => x.id === parseInt(e.target.value));
                              if (c) {
                                  setActiveCountry(c);
                              }
                          }}
                          className="bg-transparent border-none text-right font-medium font-sans text-black dark:text-white cursor-pointer focus:ring-0 w-auto text-[14px]"
                      >
                          {countries.map(c => (
                              <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
                          ))}
                      </select>
                  </div>
                  
                  <div className="px-4 py-2 flex items-center justify-between">
                    <span className="text-[14px] font-medium font-sans text-gray-500 dark:text-gray-400">{t('nav_language')}</span>
                    <LanguageSwitcher variant="default" />
                  </div>

                  {/* Appearance Section Removed */}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* ✅ GLobal AI Chat Overlay */}
      <AIChatOverlay isOpen={isAiChatOpen} onClose={() => setIsAiChatOpen(false)} />
      
    </>
  );
};

export default Navbar;
